import { observer } from 'mobx-react';
import { PropsWithChildren } from 'react';
import { ProductCollectionResource } from '../../Types';
import AddPrograms from '../AddPrograms';
import ProductCollectionTitle from './ProductCollectionTitle';
import ProductCollectionDescription from './ProductCollectionDescription';
import { useLearningCollection } from '../../LearningCollectionContext';
import { HtmlView } from '../../ExperienceHtmlView';

interface Props {
  className?: string;
  productCollection: ProductCollectionResource;
  index: number;
  totalSections: number;
  onOpenCatalog: () => void;
  // DVLP-7601: read-only variant for the admin preview — no inline text
  // editors, no AddPrograms/Delete button. The title renders as plain text;
  // the description is stored HTML, so it goes through the same HtmlView the
  // editor and the participant's Discovery Portal use (DVLP-8084).
  editable?: boolean;
}

function ExperienceSection({
  children,
  productCollection,
  index,
  onOpenCatalog,
  editable = true,
}: PropsWithChildren<Props>) {
  const { name, description } = productCollection.payload;
  const {
    handleProductCollectionNameChange,
    handleProductCollectionDescriptionChange,
    handleDeleteProductCollection,
  } = useLearningCollection();

  return (
    <div className="mx-auto xl:container px-6 md:px-14">
      <div
        className="bg-card border border-border rounded-lg p-6"
        data-testid="learning-collection-experience-section"
        data-intercom-target="learning-collection-experience-section"
      >
        <div
          data-testid="learning-collection-experience-header"
          data-intercom-target="learning-collection-experience-header"
          className="flex items-start justify-between gap-4"
        >
          <div className="flex-1 min-w-0 pr-4">
            {editable ? (
              <>
                <div className="mb-2">
                  <ProductCollectionTitle
                    name={name}
                    handleProductCollectionNameChange={(newName) =>
                      handleProductCollectionNameChange(index, newName)
                    }
                  />
                </div>
                <div>
                  <ProductCollectionDescription
                    description={description}
                    onDescriptionChange={(newDescription) =>
                      handleProductCollectionDescriptionChange(index, newDescription)
                    }
                  />
                </div>
              </>
            ) : (
              <>
                <h2
                  className="mb-2"
                  style={{
                    fontSize: 'var(--text-xl)',
                    fontWeight: 'var(--font-weight-semibold)',
                  }}
                >
                  {name}
                </h2>
                <HtmlView
                  description={description}
                  className="text-muted-foreground [&_p]:text-muted-foreground"
                />
              </>
            )}
          </div>
          {editable && (
            <div className="flex items-start gap-2 flex-shrink-0">
              <AddPrograms
                productCollection={productCollection}
                index={index}
                onOpen={onOpenCatalog}
                onDelete={() => handleDeleteProductCollection(index)}
              />
            </div>
          )}
        </div>

        <div
          data-testid="learning-collection-experience-body"
          data-intercom-target="learning-collection-experience-body"
          className="mt-4"
        >
          {children}
        </div>
      </div>
    </div>
  );
}

export default observer(ExperienceSection);
